import React, { useEffect, useState, createContext, useContext, Component } from 'react';
import { motion } from 'framer-motion';
import { Check, X, ShieldCheck, Lock, Star, Gift, BookOpen, ArrowRight, Clock, HelpCircle, CreditCard, Smartphone, Award } from 'lucide-react';
import { MegaPackComposite } from '../components/MegaPackComposite';
import { SocialProofPopup } from '../components/SocialProofPopup';
import { useLocalCurrency } from '../hooks/useLocalCurrency';
// USD base prices
const PRICE_ORIGINAL = 47;
const PRICE_OFFER = 13.99;
const CHECKOUT_URL = 'https://pay.hotmart.com/M104015955Q?checkoutMode=10';
// Shared countdown context so all timers stay in sync
const CountdownContext = createContext('10:53');
const CountdownProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [totalSeconds, setTotalSeconds] = useState(10 * 60 + 53);
  useEffect(() => {
    const t = setInterval(() => {
      setTotalSeconds(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(t);
  }, []);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const display = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return <CountdownContext.Provider value={display} data-id="element-414">
      {children}
    </CountdownContext.Provider>;
};
const CountdownDisplay: React.FC<{
  className?: string;
}> = ({
  className = ''
}) => {
  const time = useContext(CountdownContext);
  return <span className={`font-mono font-bold ${className}`} data-id="element-415">{time}</span>;
};
const ScrollCTA: React.FC<{
  text: string;
  fullWidth?: boolean;
  className?: string;
}> = ({
  text,
  fullWidth,
  className = ''
}) => <button onClick={() => {
  const section = document.getElementById('compra');
  if (section) section.scrollIntoView({
    behavior: 'smooth'
  });
}} aria-label={text} className={`inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold text-lg py-4 px-8 rounded-full shadow-lg border-b-4 border-orange-700 hover:border-orange-600 transition-all transform hover:-translate-y-1 cursor-pointer ${fullWidth ? 'w-full text-xl' : ''} ${className}`} data-id="element-416">
    <span data-id="element-417">{text}</span>
    <ArrowRight className="w-5 h-5" data-id="element-418" />
  </button>;
// Trust Badges Component
const TrustBadges = () => <div className="flex flex-wrap justify-center items-center gap-4 mt-6 opacity-80" data-id="element-419">
    <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full text-xs text-white" data-id="element-420">
      <Lock className="w-3.5 h-3.5" data-id="element-421" />
      <span data-id="element-422">Compra 100% Segura</span>
    </div>
    <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full text-xs text-white" data-id="element-423">
      <Award className="w-3.5 h-3.5" data-id="element-424" />
      <span data-id="element-425">Garantía de 30 Días</span>
    </div>
    <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full text-xs text-white" data-id="element-426">
      <ShieldCheck className="w-3.5 h-3.5" data-id="element-427" />
      <span data-id="element-428">Plataforma Hotmart</span>
    </div>
  </div>;
// Payment Methods Component
const PaymentMethods = () => <div className="mt-4 flex flex-col items-center" data-id="element-429">
    <p className="text-xs text-gray-500 mb-2 font-medium" data-id="element-430">
      Opciones de pago disponibles en tu país:
    </p>
    <div className="flex items-center justify-center gap-3 text-gray-400" data-id="element-431">
      <CreditCard className="w-6 h-6" data-id="element-432" />
      <Smartphone className="w-6 h-6" data-id="element-433" />
      <span className="text-xs font-bold border border-gray-300 px-2 py-0.5 rounded" data-id="element-434">
        VISA
      </span>
      <span className="text-xs font-bold border border-gray-300 px-2 py-0.5 rounded" data-id="element-435">
        MASTERCARD
      </span>
      <span className="text-xs font-bold border border-gray-300 px-2 py-0.5 rounded" data-id="element-436">
        AMEX
      </span>
    </div>
  </div>;
export const ShortSalesPage: React.FC = () => {
  const {
    formatPrice,
    formatPriceCompact,
    currencyCode,
    countryFlag,
    isUSD,
    isLoading
  } = useLocalCurrency();
  return <CountdownProvider data-id="element-437">
      <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-438">
        {/* ── URGENCY BAR (sticky) ── */}
        <div className="bg-teal-800 text-white text-center py-2.5 px-4 text-sm font-medium sticky top-0 z-40 flex items-center justify-center gap-3" data-id="element-439">
          <Clock className="w-4 h-4 text-orange-400 flex-shrink-0" data-id="element-440" />
          <span data-id="element-441">
            Precio de lanzamiento termina en:{' '}
            <CountdownDisplay className="text-orange-300" data-id="element-442" />
          </span>
          <button onClick={() => {
          const section = document.getElementById('compra');
          if (section) section.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="hidden md:inline bg-orange-500 hover:bg-orange-400 text-white font-bold text-xs px-3 py-1 rounded-full transition-colors ml-2 cursor-pointer" data-id="element-443">
            Acceder ahora →
          </button>
        </div>

        {/* ── 1. HERO ── */}
        <section className="relative bg-slate-50 overflow-hidden pt-10 pb-16 md:pt-16 md:pb-24" data-id="element-444">
          <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-445">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-300 blur-3xl" data-id="element-446" />
            <div className="absolute top-1/2 -left-24 w-72 h-72 rounded-full bg-teal-300 blur-3xl" data-id="element-447" />
          </div>
          <div className="container mx-auto px-4 relative z-10 max-w-4xl" data-id="element-448">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-center" data-id="element-449">
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-semibold mb-5" data-id="element-450">
                <Star className="w-4 h-4 fill-current" data-id="element-451" />
                <span data-id="element-452">+3.247 docentes ya lo usan • Descarga inmediata</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-8" data-id="element-453">
                Ciencias Naturales{' '}
                <span className="text-teal-600" data-id="element-454">listas para todo el año</span>
              </h1>

              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.7,
              delay: 0.2
            }} className="mb-10 max-w-2xl mx-auto" data-id="element-455">
                <MegaPackComposite size="normal" data-id="element-456" />
              </motion.div>

              <p className="text-lg md:text-xl text-gray-600 mb-6 leading-relaxed max-w-2xl mx-auto" data-id="element-457">
                El sistema completo para organizar todas tus clases en una sola
                tarde.{' '}
                <span className="font-semibold text-gray-800" data-id="element-458">
                  Sin improvisar. Sin buscar más.
                </span>
              </p>

              <ul className="space-y-2.5 mb-8 text-left max-w-md mx-auto" data-id="element-459">
                {['Planificación anual + clases desarrolladas', 'Experimentos simples sin laboratorio', 'Evaluaciones A/B listas por unidad', '4 Bonos incluidos sin costo adicional'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-460">
                    <div className="bg-teal-100 p-0.5 rounded-full flex-shrink-0" data-id="element-461">
                      <Check className="w-4 h-4 text-teal-600" data-id="element-462" />
                    </div>
                    <span className="text-gray-700 font-medium" data-id="element-463">{item}</span>
                  </li>)}
              </ul>

              <ScrollCTA text="Sí, quiero el MegaPack ahora" data-id="element-464" />

              <div className="mt-5 flex items-center justify-center gap-4 text-sm text-gray-500" data-id="element-465">
                <span className="flex items-center gap-1" data-id="element-466">
                  <Lock className="w-3 h-3" data-id="element-467" /> Pago seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-468">
                  <ShieldCheck className="w-3 h-3 text-teal-600" data-id="element-469" /> Garantía 30
                  días
                </span>
                <span data-id="element-470">Acceso inmediato</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── 2. PAIN → GAIN ── */}
        <section className="py-14 bg-white" data-id="element-471">
          <div className="container mx-auto px-4 max-w-5xl" data-id="element-472">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10" data-id="element-473">
              ¿Te suena familiar?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-474">
              <div className="bg-red-50 border border-red-100 rounded-2xl p-6" data-id="element-475">
                <h3 className="font-bold text-red-700 mb-4 flex items-center gap-2 text-lg" data-id="element-476">
                  <X className="w-5 h-5" data-id="element-477" /> Sin el MegaPack
                </h3>
                <ul className="space-y-3" data-id="element-478">
                  {['Buscás recursos por todos lados sin encontrar nada útil', 'Improvisás cada semana y el año no tiene estructura', 'Los experimentos no siempre funcionan', 'Evaluar te lleva horas porque hay que inventar todo', 'Terminás agotado/a sin tiempo para nada más'].map((t, i) => <li key={i} className="flex items-start gap-2 text-gray-600 text-sm" data-id="element-479">
                      <X className="w-4 h-4 text-red-300 mt-0.5 flex-shrink-0" data-id="element-480" />
                      {t}
                    </li>)}
                </ul>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 shadow-md" data-id="element-481">
                <h3 className="font-bold text-teal-600 mb-4 flex items-center gap-2 text-lg" data-id="element-482">
                  <Check className="w-5 h-5" data-id="element-483" /> Con el MegaPack
                </h3>
                <ul className="space-y-3" data-id="element-484">
                  {['Abrís una unidad y ya tenés todo listo para dar clase', 'Seguís una planificación anual clara desde el día 1', 'Experimentos simples con materiales comunes', 'Evaluaciones con versiones A/B y rúbricas incluidas', 'Más tiempo, menos estrés y clases de mayor calidad'].map((t, i) => <li key={i} className="flex items-start gap-2 text-gray-800 text-sm font-medium" data-id="element-485">
                      <Check className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" data-id="element-486" />
                      {t}
                    </li>)}
                </ul>
              </div>
            </div>
            <div className="mt-10 text-center" data-id="element-487">
              <ScrollCTA text="Quiero dejar de improvisar" data-id="element-488" />
            </div>
          </div>
        </section>

        {/* ── 3. VALUE STACK ── */}
        <section className="py-14 bg-slate-50" data-id="element-489">
          <div className="container mx-auto px-4 max-w-3xl" data-id="element-490">
            <div className="text-center mb-8" data-id="element-491">
              <span className="inline-block bg-orange-100 text-orange-800 px-4 py-1 rounded-full font-bold text-sm mb-3" data-id="element-492">
                TODO LO QUE RECIBÍS
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-493">
                Un sistema completo, no un PDF suelto
              </h2>
            </div>

            <div className="space-y-3 mb-8" data-id="element-494">
              {[{
              label: 'MegaPack Ciencias Naturales (5 tomos)',
              usd: 47
            }, {
              label: 'BONO 1 — Banco de Experimentos Express',
              usd: 27
            }, {
              label: 'BONO 2 — Evaluaciones Premium A/B',
              usd: 19
            }, {
              label: 'BONO 3 — Kit Visual Científico',
              usd: 15
            }, {
              label: 'BONO 4 — Planificación Perfecta Ciencias',
              usd: 19
            }].map((item, i) => <div key={i} className={`flex items-center justify-between p-4 rounded-xl border ${i === 0 ? 'bg-emerald-50 border-emerald-200 font-semibold' : 'bg-white border-slate-200'}`} data-id="element-495">
                  <div className="flex items-center gap-3" data-id="element-496">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-teal-600 text-white' : 'bg-orange-100 text-orange-700'}`} data-id="element-497">
                      {i === 0 ? <BookOpen className="w-4 h-4" data-id="element-498" /> : <Gift className="w-3.5 h-3.5" data-id="element-499" />}
                    </div>
                    <span className="text-gray-800 text-sm" data-id="element-500">{item.label}</span>
                  </div>
                  <span className="text-gray-400 line-through text-sm font-medium" data-id="element-501">
                    {formatPriceCompact(item.usd)}
                  </span>
                </div>)}
            </div>

            <div className="bg-teal-800 text-white rounded-2xl p-6 text-center shadow-xl" data-id="element-502">
              <p className="text-teal-300 text-sm mb-3" data-id="element-503">
                Valor real total:{' '}
                <span className="line-through" data-id="element-504">{formatPrice(127)}</span>
              </p>

              {/* Localized price display */}
              <div className="mb-1" data-id="element-505">
                <span className="text-3xl" data-id="element-506">{countryFlag}</span>
                {!isUSD && <p className="text-xs text-teal-400 mt-1" data-id="element-507">
                    Precio en tu moneda
                  </p>}
              </div>
              <p className="text-5xl md:text-6xl font-extrabold text-white mb-1" data-id="element-508">
                {formatPrice(PRICE_OFFER)}
              </p>
              <p className="text-teal-300 text-sm mb-1" data-id="element-509">
                Pago único • Acceso de por vida • Descarga inmediata
              </p>
              {!isUSD && <p className="text-xs text-teal-400/70 mb-4" data-id="element-510">
                  Precio aproximado en {currencyCode} · El cobro final se
                  realiza en USD
                </p>}
              {isUSD && <div className="mb-4" data-id="element-511" />}

              <ScrollCTA text="Acceder al MegaPack con Descuento" fullWidth data-id="element-512" />
              <p className="mt-3 text-xs text-teal-400 flex items-center justify-center gap-1" data-id="element-513">
                <Lock className="w-3 h-3" data-id="element-514" /> Pago 100% Seguro y Encriptado
              </p>
            </div>
          </div>
        </section>

        {/* ── 4. SOCIAL PROOF ── */}
        <section className="py-14 bg-white" data-id="element-515">
          <div className="container mx-auto px-4 max-w-5xl" data-id="element-516">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-2" data-id="element-517">
              Docentes que ya lo usan
            </h2>
            <p className="text-center text-gray-500 mb-8 text-sm" data-id="element-518">
              Resultados reales de docentes de toda Latinoamérica
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-519">
              {[{
              name: 'María C.',
              role: 'Ciencias Naturales, Argentina',
              text: 'Las guías son clarísimas. Me ahorró muchísimo tiempo y las clases salen mejor.',
              color: 'bg-sky-50 border-sky-100'
            }, {
              name: 'Jorge A.',
              role: 'Secundaria, Chile',
              text: 'Los experimentos simples fueron clave. Participan más y entienden mejor.',
              color: 'bg-emerald-50 border-emerald-100'
            }, {
              name: 'Sandra R.',
              role: 'Primaria, México',
              text: 'Abrís una unidad y ya tenés todo listo. Súper organizado.',
              color: 'bg-orange-50 border-orange-100'
            }].map((t, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className={`p-5 rounded-xl border ${t.color}`} data-id="element-520">
                  <div className="flex gap-1 mb-3" data-id="element-521">
                    {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-522" />)}
                  </div>
                  <p className="text-gray-700 italic text-sm mb-4" data-id="element-523">
                    "{t.text}"
                  </p>
                  <div className="flex items-center gap-2" data-id="element-524">
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xs" data-id="element-525">
                      {t.name.charAt(0)}
                    </div>
                    <div data-id="element-526">
                      <p className="font-bold text-gray-900 text-sm" data-id="element-527">
                        {t.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-528">{t.role}</p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* ── 4.5 MINI FAQ (TRUST ELEMENT) ── */}
        <section className="py-14 bg-slate-50 border-t border-slate-200" data-id="element-529">
          <div className="container mx-auto px-4 max-w-3xl" data-id="element-530">
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-8" data-id="element-531">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-4" data-id="element-532">
              {[{
              q: '¿Cómo recibo el material?',
              a: 'Inmediatamente después de tu pago seguro, recibirás un correo electrónico con el enlace para descargar todos los archivos en formato PDF, listos para imprimir o usar digitalmente.'
            }, {
              q: '¿Es un pago único o mensual?',
              a: 'Es un pago ÚNICO. No hay suscripciones ni cobros ocultos. Pagas una vez y tienes acceso de por vida al material y sus futuras actualizaciones.'
            }, {
              q: '¿Qué pasa si no me sirve?',
              a: 'Tienes 30 días de garantía incondicional. Si descargas el material y sientes que no se adapta a tus clases, nos envías un correo y te devolvemos el 100% de tu dinero sin hacer preguntas.'
            }, {
              q: '¿Es seguro pagar online?',
              a: 'Totalmente. El pago es procesado por Hotmart, la plataforma de educación digital más grande y segura de Latinoamérica. Nosotros nunca vemos los datos de tu tarjeta.'
            }].map((faq, i) => <div key={i} className="bg-white p-5 rounded-xl shadow-sm border border-slate-100" data-id="element-533">
                  <h3 className="font-bold text-gray-900 flex items-start gap-2 mb-2" data-id="element-534">
                    <HelpCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" data-id="element-535" />
                    {faq.q}
                  </h3>
                  <p className="text-gray-600 text-sm ml-7" data-id="element-536">{faq.a}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* ── 5. URGENCY + COUNTDOWN ── */}
        <section className="py-12 bg-orange-50 border-y border-orange-200" data-id="element-537">
          <div className="container mx-auto px-4 text-center max-w-2xl" data-id="element-538">
            <p className="text-orange-800 font-bold text-lg mb-3" data-id="element-539">
              ⚠️ El precio de lanzamiento termina en:
            </p>
            <div className="text-5xl md:text-6xl font-black text-orange-600 font-mono mb-4" data-id="element-540">
              <CountdownDisplay className="text-orange-600" data-id="element-541" />
            </div>
            <p className="text-gray-600 mb-6" data-id="element-542">
              Cuando llegue a cero, el precio promocional desaparecerá.
            </p>
            <ScrollCTA text="ASEGURAR MI ACCESO AHORA" data-id="element-543" />
          </div>
        </section>

        {/* ── 6. FINAL PRICING + GUARANTEE ── */}
        <section id="compra" className="py-14 bg-teal-800" data-id="element-544">
          <div className="container mx-auto px-4 max-w-2xl" data-id="element-545">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-546">
              <div className="bg-orange-400 p-5 text-center" data-id="element-547">
                <h2 className="text-xl md:text-2xl font-bold text-orange-900" data-id="element-548">
                  Accedé hoy con precio de lanzamiento
                </h2>
                <p className="text-orange-800 text-sm font-medium" data-id="element-549">
                  Esta promoción puede finalizar en cualquier momento
                </p>
              </div>
              <div className="p-8 text-center" data-id="element-550">
                {/* Product image above price */}
                <div className="mb-6" data-id="element-551">
                  <MegaPackComposite size="small" data-id="element-552" />
                </div>

                {/* Localized price block */}
                <div className="mb-2" data-id="element-553">
                  <span className="text-4xl" data-id="element-554">{countryFlag}</span>
                  {!isUSD && <p className="text-xs text-gray-500 mt-1" data-id="element-555">
                      Precio en tu moneda
                    </p>}
                </div>

                <div className="flex justify-center items-center gap-3 mb-3" data-id="element-556">
                  <span className="text-gray-400 text-lg line-through" data-id="element-557">
                    {formatPrice(PRICE_ORIGINAL)}
                  </span>
                  <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full font-bold text-sm" data-id="element-558">
                    AHORRAS{' '}
                    {Math.round((1 - PRICE_OFFER / PRICE_ORIGINAL) * 100)}%
                  </span>
                </div>

                <div className="mb-1" data-id="element-559">
                  <span className="text-5xl md:text-6xl font-extrabold text-teal-600" data-id="element-560">
                    {formatPrice(PRICE_OFFER)}
                  </span>
                </div>
                <span className="text-gray-500 text-lg block mt-1 mb-1" data-id="element-561">
                  Pago único • Acceso de por vida
                </span>
                {!isUSD && <p className="text-xs text-gray-400 mb-3" data-id="element-562">
                    Precio aproximado en {currencyCode} · El cobro final se
                    realiza en USD
                  </p>}

                {/* Countdown in pricing */}
                <div className="mb-6 flex items-center justify-center gap-2 text-sm text-red-600 font-semibold" data-id="element-563">
                  <Clock className="w-4 h-4" data-id="element-564" />
                  <span data-id="element-565">
                    Oferta termina en:{' '}
                    <CountdownDisplay className="text-red-600" data-id="element-566" />
                  </span>
                </div>

                <div className="space-y-3 mb-8 max-w-xs mx-auto text-left" data-id="element-567">
                  {['Acceso inmediato al MegaPack completo', 'Los 4 Bonos incluidos', 'Material 100% imprimible', 'Actualizaciones futuras gratuitas'].map((item, i) => <div key={i} className="flex items-center gap-3" data-id="element-568">
                      <div className="bg-teal-100 p-1 rounded-full flex-shrink-0" data-id="element-569">
                        <Check className="w-4 h-4 text-teal-600" data-id="element-570" />
                      </div>
                      <span className="text-gray-700 text-sm" data-id="element-571">{item}</span>
                    </div>)}
                </div>

                <a href={CHECKOUT_URL} rel="noopener noreferrer" className="w-full block bg-orange-500 hover:bg-orange-400 text-white font-bold text-xl py-4 rounded-xl shadow-lg border-b-4 border-orange-700 hover:border-orange-600 transition-all transform hover:-translate-y-1 mb-3" data-id="element-572">
                  Acceder al MegaPack con Descuento →
                </a>

                <p className="text-xs text-gray-400 flex items-center justify-center gap-1" data-id="element-573">
                  <Lock className="w-3 h-3" data-id="element-574" /> Pago 100% Seguro Procesado por
                  Hotmart
                </p>

                <PaymentMethods data-id="element-575" />
              </div>

              {/* Enhanced Guarantee */}
              <div className="bg-slate-50 p-6 border-t border-slate-200" data-id="element-576">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left" data-id="element-577">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-578">
                    <ShieldCheck className="w-8 h-8 text-teal-600" data-id="element-579" />
                  </div>
                  <div data-id="element-580">
                    <h4 className="font-bold text-gray-900 text-base mb-1" data-id="element-581">
                      Garantía de Satisfacción Total — 30 días
                    </h4>
                    <p className="text-sm text-gray-600 mb-2" data-id="element-582">
                      Tu compra está 100% protegida. Si descargas el pack y
                      sientes que no te sirve para tu realidad docente, tienes
                      30 días para pedir la devolución.
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1 text-left inline-block sm:block" data-id="element-583">
                      <li className="flex items-center gap-1.5" data-id="element-584">
                        <Check className="w-3 h-3 text-teal-500" data-id="element-585" /> Reembolso
                        automático
                      </li>
                      <li className="flex items-center gap-1.5" data-id="element-586">
                        <Check className="w-3 h-3 text-teal-500" data-id="element-587" /> Sin
                        preguntas ni trabas
                      </li>
                      <li className="flex items-center gap-1.5" data-id="element-588">
                        <Check className="w-3 h-3 text-teal-500" data-id="element-589" /> Riesgo cero
                        para ti
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <TrustBadges data-id="element-590" />
          </div>
        </section>

        <footer className="bg-slate-900 text-slate-400 py-8 text-center text-xs" data-id="element-591">
          <div className="container mx-auto px-4 max-w-4xl flex flex-col items-center" data-id="element-592">
            <div className="flex items-center justify-center gap-2 mb-4 text-slate-500" data-id="element-593">
              <Lock className="w-4 h-4" data-id="element-594" />
              <span data-id="element-595">Sitio seguro con encriptación SSL de 256 bits</span>
            </div>
            <p className="mb-2" data-id="element-596">
              © {new Date().getFullYear()} MegaPack Ciencias Naturales. Todos
              los derechos reservados.
            </p>
            <p className="mb-4" data-id="element-597">
              Este sitio no forma parte del sitio web de Facebook o Facebook
              Inc. Adicionalmente, este sitio NO está respaldado por Facebook de
              ninguna manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
            </p>
            <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-598">
              Hotmart es la plataforma encargada de la distribución de los
              productos digitales. Al comprar, estás realizando una transacción
              100% segura avalada por los estándares internacionales de
              seguridad.
            </p>
          </div>
        </footer>

        <SocialProofPopup data-id="element-599" />
      </div>
    </CountdownProvider>;
};